import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Upload, FileText, Check, XCircle } from "lucide-react";
import { toast } from "sonner";
import {
  getArticles, getFormationsAdmin, getRapports, getActivites, getTemoignages, getMessagesAdmin, getPartenairesAdmin, getBenevolesAdmin, getInscriptionsAdmin, getInfolettreAdmin, setPartenaireStatut,
  getMe, adminCreate, adminUpdate, adminDelete, clearToken, formatApiError, uploadRapportPdf, uploadActivitePhoto, deleteActivitePhoto,
} from "@/lib/api";
import { ADMIN_LOGIN, ADMIN_MDP } from "@/lib/adminPath";
import { ArticleForm } from "@/components/admin/ArticleForm";
import { FormationForm } from "@/components/admin/FormationForm";
import { RapportForm } from "@/components/admin/RapportForm";
import { ActiviteForm } from "@/components/admin/ActiviteForm";
import { TemoignageForm } from "@/components/admin/TemoignageForm";
import { AdminBourses } from "@/pages/admin/AdminBourses";


const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TABS = [
  {
    id: "articles", label: "Articles", fetch: getArticles, Form: ArticleForm, nouveau: "Nouvel article",
    titre: (a) => a.titre,
    resume: (a) => `${a.categorie} · ${a.date}`,
  },
  {
    id: "formations", label: "Formations", fetch: getFormationsAdmin, Form: FormationForm, nouveau: "Nouvelle formation",
    titre: (f) => f.titre,
    resume: (f) => `${f.categorie} · ${f.prix == null ? "Prix sur Eventbrite" : f.prix === 0 ? "Gratuite" : `${f.prix} $`} · ${f.prochaine_session}${f.lien ? " · Eventbrite" : ""}`,
  },
  {
    id: "temoignages", label: "Témoignages", fetch: getTemoignages, Form: TemoignageForm, nouveau: "Nouveau témoignage",
    titre: (t) => t.nom,
    resume: (t) => t.role,
  },
  {
    id: "rapports", label: "Rapports annuels", fetch: getRapports, Form: RapportForm, nouveau: "Nouveau rapport", pdf: true,
    titre: (r) => r.titre,
    resume: (r) => `${r.annee} · ${r.points_saillants.length} points saillants${r.pdf_filename ? ` · PDF : ${r.pdf_filename}` : ""}`,
  },
  {
    id: "activites", label: "Activités", fetch: getActivites, Form: ActiviteForm, nouveau: "Nouvelle activité", photos: true,
    titre: (a) => a.titre,
    resume: (a) => `${a.date} · ${a.lieu}`,
  },
  {
    id: "bourses", label: "Bourses", custom: true,
  },
  {
    id: "messages", label: "Messages reçus", fetch: getMessagesAdmin, lectureSeule: true,
    titre: (m) => m.sujet,
    resume: (m) => `${m.nom} · ${m.courriel}${m.telephone ? ` · ${m.telephone}` : ""} · ${new Date(m.date).toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" })}`,
  },
  {
    id: "partenaires", label: "Partenaires", fetch: getPartenairesAdmin, lectureSeule: true, approbation: true,
    titre: (p) => p.organisation || p.nom,
    resume: (p) => `${p.nom} · ${p.courriel}${p.telephone ? ` · ${p.telephone}` : ""}${p.type_partenariat ? ` · ${p.type_partenariat}` : ""} · ${new Date(p.date).toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" })}`,
    statut: (p) => p.statut || "en_attente",
    liens: (p) => (p.logo_path ? [{ label: `Logo — ${p.logo_nom || "voir"}`, url: `${BACKEND}/api/media/${p.logo_path}` }] : []),
  },
  {
    id: "benevoles", label: "Bénévoles", fetch: getBenevolesAdmin, lectureSeule: true,
    titre: (b) => b.nom,
    resume: (b) => `${b.domaines} · ${b.courriel}${b.disponibilites ? ` · Dispo : ${b.disponibilites}` : ""}${b.duree_engagement ? ` · Engagement : ${b.duree_engagement}` : ""} · ${new Date(b.date).toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" })}`,
    details: (b) => [
      b.services_similaires ? `Services similaires déjà offerts : ${b.services_similaires === "oui" ? "Oui" : "Non"}${b.services_similaires === "oui" && b.services_similaires_details ? ` — ${b.services_similaires_details}` : ""}` : null,
      b.reference1_nom ? `Référence 1 : ${b.reference1_nom} — ${b.reference1_contact}` : null,
      b.reference2_nom ? `Référence 2 : ${b.reference2_nom} — ${b.reference2_contact}` : null,
    ].filter(Boolean),
    liens: (b) => [
      b.cv_path ? { label: `CV — ${b.cv_nom || "télécharger"}`, url: `${BACKEND}/api/media/${b.cv_path}` } : null,
      b.lettre_path ? { label: `Lettre — ${b.lettre_nom || "télécharger"}`, url: `${BACKEND}/api/media/${b.lettre_path}` } : null,
    ].filter(Boolean),
  },
  {
    id: "inscriptions", label: "Inscriptions", fetch: getInscriptionsAdmin, lectureSeule: true,
    titre: (i) => i.formation_titre,
    resume: (i) => `${i.nom} · ${i.courriel} · ${i.statut === "confirmee" ? "Confirmée" : "En attente de paiement"}${i.montant ? ` · ${i.montant} $` : ""} · ${new Date(i.date).toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" })}`,
  },
  {
    id: "infolettre", label: "Infolettre", fetch: getInfolettreAdmin, lectureSeule: true,
    titre: (a) => a.courriel,
    resume: (a) => `${a.actif ? "Abonné" : "Désabonné"} · inscription le ${new Date(a.date_inscription).toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" })}`,
  },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [onglet, setOnglet] = useState("articles");
  const [items, setItems] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [edition, setEdition] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [admin, setAdmin] = useState(null);
  const fileRefs = useRef({});
  const photoRefs = useRef({});

  const tab = TABS.find((t) => t.id === onglet);

  const charger = useCallback(() => {
    if (tab.custom) {
      setItems([]);
      setChargement(false);
      return;
    }
    setChargement(true);
    tab.fetch()
      .then(setItems)
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setChargement(false));
  }, [tab]);

  useEffect(() => {
    getMe()
      .then((me) => {
        if (me.must_change_password) {
          navigate(ADMIN_MDP);
          return;
        }
        setAdmin(me);
      })
      .catch(() => {
        clearToken();
        navigate(ADMIN_LOGIN);
      });
  }, [navigate]);

  useEffect(() => {
    charger();
  }, [charger]);

  const sauvegarder = async (data) => {
    setSaving(true);
    try {
      if (edition) await adminUpdate(tab.id, edition.id, data);
      else await adminCreate(tab.id, data);
      toast.success(edition ? "Modifications enregistrées" : "Élément créé");
      setEdition(undefined);
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changerStatut = async (item, statut) => {
    try {
      await setPartenaireStatut(item.id, statut);
      toast.success(statut === "approuve" ? "Partenaire approuvé — visible dans « Nos Partenaires »" : "Candidature rejetée");
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const supprimer = async (item) => {
    if (!window.confirm(`Supprimer définitivement « ${tab.titre(item)} » ?`)) return;
    try {
      await adminDelete(tab.id, item.id);
      toast.success("Élément supprimé");
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const envoyerPdf = async (item, file) => {
    if (!file) return;
    setUploading(item.id);
    try {
      await uploadRapportPdf(item.id, file);
      toast.success("PDF téléversé — il est maintenant téléchargeable sur la page Notre impact");
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(null);
    }
  };

  const envoyerPhoto = async (item, file) => {
    if (!file) return;
    setUploading(item.id);
    try {
      await uploadActivitePhoto(item.id, file);
      toast.success("Photo ajoutée à la frise");
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(null);
    }
  };

  const supprimerPhoto = async (item, index) => {
    try {
      await deleteActivitePhoto(item.id, index);
      toast.success("Photo retirée");
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const FormComponent = tab.Form;

  return (
    <section className="bg-background py-16 md:py-24" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Espace admin</p>
            <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight md:text-5xl" data-testid="admin-dashboard-title">
              Gestion du contenu
            </h1>
            {admin && <p className="mt-2 text-sm text-foreground/50">Connecté en tant que {admin.email}</p>}
          </div>
          <button
            onClick={() => { clearToken(); navigate(ADMIN_LOGIN); }}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 rounded-sm border border-foreground/20 px-5 py-2.5 text-sm font-semibold transition-colors hover:border-primary hover:text-primary"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </div>

        <div className="mt-12 flex flex-wrap gap-3" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => { setOnglet(t.id); setEdition(undefined); setItems([]); }}
              data-testid={`admin-tab-${t.id}`}
              className={`rounded-sm border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                onglet === t.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground/70 hover:border-primary hover:text-primary"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {!tab.lectureSeule && !tab.custom && (
          <div className="mt-8 flex justify-end">
            <button
              onClick={() => setEdition(null)}
              data-testid="admin-new-button"
              className="flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)]"
            >
              <Plus size={16} /> {tab.nouveau}
            </button>
          </div>
        )}

        {tab.custom ? (
          <AdminBourses />
        ) : chargement ? (
          <p className="mt-12 text-foreground/50" data-testid="admin-loading">Chargement…</p>
        ) : (
          <div className="mt-6" data-testid="admin-items-list">
            {items.map((item, i) => (
              <div key={item.id} className="border-t border-border py-6" data-testid={`admin-item-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="truncate font-serif text-xl font-semibold">{tab.titre(item)}</h3>
                      {tab.approbation && (
                        <span
                          data-testid={`admin-item-${i}-statut`}
                          className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                            (tab.statut(item)) === "approuve"
                              ? "bg-green-100 text-green-800"
                              : (tab.statut(item)) === "rejete"
                                ? "bg-red-100 text-red-800"
                                : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {({ en_attente: "En attente", approuve: "Approuvé", rejete: "Rejeté" })[tab.statut(item)]}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-foreground/50">{tab.resume(item)}</p>
                    {tab.details && tab.details(item).map((l, j) => (
                      <p key={j} className="mt-1 text-xs text-foreground/60" data-testid={`admin-item-${i}-detail-${j}`}>{l}</p>
                    ))}
                    {tab.liens && (
                      <div className="mt-2 flex flex-wrap gap-3">
                        {tab.liens(item).map((l, j) => (
                          <a key={j} href={l.url} target="_blank" rel="noopener" className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline" data-testid={`admin-item-${i}-doc-${j}`}>
                            <FileText size={13} /> {l.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tab.pdf && (
                      <>
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          ref={(el) => (fileRefs.current[item.id] = el)}
                          onChange={(e) => { envoyerPdf(item, e.target.files[0]); e.target.value = ""; }}
                          data-testid={`admin-pdf-input-${i}`}
                        />
                        <button
                          onClick={() => fileRefs.current[item.id]?.click()}
                          disabled={uploading === item.id}
                          data-testid={`admin-pdf-button-${i}`}
                          className="flex items-center gap-2 rounded-sm border border-foreground/20 px-4 py-2 text-xs font-semibold transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
                        >
                          {item.pdf_path ? <FileText size={14} /> : <Upload size={14} />}
                          {uploading === item.id ? "Envoi…" : item.pdf_path ? "Remplacer le PDF" : "Ajouter un PDF"}
                        </button>
                      </>
                    )}
                    {tab.photos && (
                      <>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          ref={(el) => (photoRefs.current[item.id] = el)}
                          onChange={(e) => { envoyerPhoto(item, e.target.files[0]); e.target.value = ""; }}
                          data-testid={`admin-photo-input-${i}`}
                        />
                        <button
                          onClick={() => photoRefs.current[item.id]?.click()}
                          disabled={uploading === item.id}
                          data-testid={`admin-photo-button-${i}`}
                          className="flex items-center gap-2 rounded-sm border border-foreground/20 px-4 py-2 text-xs font-semibold transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
                        >
                          <Upload size={14} />
                          {uploading === item.id ? "Envoi…" : "Ajouter une photo"}
                        </button>
                      </>
                    )}
                    {tab.approbation && tab.statut(item) !== "approuve" && (
                      <button
                        onClick={() => changerStatut(item, "approuve")}
                        data-testid={`admin-approve-${i}`}
                        className="flex items-center gap-2 rounded-sm border border-green-600/40 px-4 py-2 text-xs font-semibold text-green-700 transition-colors hover:bg-green-600 hover:text-white"
                      >
                        <Check size={14} /> Approuver
                      </button>
                    )}
                    {tab.approbation && tab.statut(item) !== "rejete" && (
                      <button
                        onClick={() => changerStatut(item, "rejete")}
                        data-testid={`admin-reject-${i}`}
                        className="flex items-center gap-2 rounded-sm border border-destructive/40 px-4 py-2 text-xs font-semibold text-destructive transition-colors hover:bg-destructive hover:text-destructive-foreground"
                      >
                        <XCircle size={14} /> Rejeter
                      </button>
                    )}
                    {!tab.lectureSeule && (
                      <button
                        onClick={() => setEdition(item)}
                        data-testid={`admin-edit-${i}`}
                        className="flex items-center gap-2 rounded-sm border border-foreground/20 px-4 py-2 text-xs font-semibold transition-colors hover:border-primary hover:text-primary"
                      >
                        <Pencil size={14} /> Modifier
                      </button>
                    )}
                    <button
                      onClick={() => supprimer(item)}
                      data-testid={`admin-delete-${i}`}
                      className="flex items-center gap-2 rounded-sm border border-destructive/40 px-4 py-2 text-xs font-semibold text-destructive transition-colors hover:bg-destructive hover:text-destructive-foreground"
                    >
                      <Trash2 size={14} /> Supprimer
                    </button>
                  </div>
                </div>
                {tab.lectureSeule && item.message && (
                  <p className="mt-4 max-w-3xl whitespace-pre-line text-sm leading-relaxed text-foreground/70" data-testid={`admin-message-body-${i}`}>
                    {item.message}
                  </p>
                )}
                {tab.photos && item.photos?.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-3" data-testid={`admin-photos-${i}`}>
                    {item.photos.map((photo, j) => (
                      <div key={photo} className="relative overflow-hidden border border-border">
                        <img src={`${BACKEND}/api/media/${photo}`} alt={`${item.titre} — photo ${j + 1}`} className="h-24 w-32 object-cover" data-testid={`admin-photo-${i}-${j}`} />
                        <button
                          onClick={() => supprimerPhoto(item, j)}
                          aria-label={`Retirer la photo ${j + 1}`}
                          data-testid={`admin-photo-delete-${i}-${j}`}
                          className="absolute right-1 top-1 rounded-full bg-espresso/80 p-1 text-ivory transition-colors hover:bg-destructive"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="border-t border-border" />
            {items.length === 0 && <p className="py-12 text-foreground/50" data-testid="admin-empty">Aucun élément pour le moment.</p>}
          </div>
        )}
      </div>

      {edition !== undefined && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-espresso/70 backdrop-blur-sm" data-testid="admin-form-modal">
          <div className="mx-auto my-10 max-w-2xl border border-border bg-background p-8 md:p-10">
            <h2 className="font-serif text-3xl font-semibold" data-testid="admin-form-title">
              {edition ? "Modifier" : tab.nouveau}
            </h2>
            <div className="mt-8">
              <FormComponent initial={edition || null} onSave={sauvegarder} onCancel={() => setEdition(undefined)} saving={saving} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
